/**
 * Typed API client for the 1TOALL portal backend.
 *
 * - Base URL is VITE_API_URL; authenticated routes live under /api/v1, health is unprefixed.
 * - A Bearer access token (Auth0, or a mock token) is attached to every authenticated call via an
 *   injectable token getter — so the client never imports Auth0 directly and stays testable.
 * - Every non-2xx response is normalized into a single ApiError shape, tolerating BOTH backend error
 *   envelopes ({error,message,detail} and the FastAPI {detail} default). For sync-now the 429/502
 *   bodies are STRUCTURED outcomes (status/quota/reason), so the client returns the parsed envelope
 *   rather than throwing — the caller renders the block loudly.
 */

import { API_URL } from "@/lib/env";
import { readRenewalFailure, type RenewalFailure } from "@/context/tokenRenewal";
import type {
  ActiveUpdateBody,
  ActivityFeed,
  ActivityParams,
  AddonWasteWorklist,
  LapsedTrialWorklist,
  AdminUser,
  AdminUsers,
  ApiError,
  DeleteUserResult,
  InviteResult,
  RosterSyncResult,
  CustomerActivation,
  CustomerInvoices,
  CustomerOverview,
  CustomerReport,
  CustomerReportInsights,
  CustomerSubscription,
  CustomerUsage,
  CustomerUsers,
  HealthReady,
  InvoiceDetail,
  Me,
  MirrorHealth,
  PoolAccess,
  PoolAccountCandidates,
  PoolAccounts,
  PoolAddAccountResult,
  PoolAddCredentialBody,
  PoolAddOperatorBody,
  PoolAudit,
  PoolAuditParams,
  PoolBulkForceAction,
  PoolBulkForceResult,
  PoolCloseBody,
  PoolCreatePeriodBody,
  PoolCreatePeriodResult,
  PoolCustomerHistory,
  PoolDashboard,
  PoolForceActionResult,
  PoolIntents,
  PoolOperatorRole,
  PoolOperators,
  PoolRemoveAccountResult,
  PoolRenewBody,
  PoolRenewDecisionBody,
  PoolRenewDecisionResult,
  PoolRenewResult,
  PoolReportDayDetail,
  PoolReportsDaily,
  PoolReportsDailyParams,
  PoolResolveIntentBody,
  PoolResolveIntentResult,
  PoolSetSystemStateBody,
  PoolSetSystemStateResult,
  PoolStatus,
  PoolSweepEnqueueResult,
  PoolSystemState,
  PoolUpdatePeriodBody,
  PoolUserCandidates,
  PoolUsers,
  ReportParams,
  RoleUpdateBody,
  SearchResult,
  SyncNowEnvelope,
  Worklist,
  WorklistKind,
  WorklistSummary,
} from "./types";

export type TokenGetter = () => Promise<string | null>;

/** Default: no token (anonymous). Replaced at app start by the Auth0/mock token getter. */
let tokenGetter: TokenGetter = async () => null;

/** Wire the access-token source (Auth0 `getAccessTokenSilently`, or the mock token). */
export function setTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter;
}

export class ApiRequestError extends Error {
  readonly error: ApiError;
  constructor(error: ApiError) {
    super(error.message);
    this.name = "ApiRequestError";
    this.error = error;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Normalize any non-2xx response (either backend envelope, or the FastAPI {detail} default).
 * `authAttempted` records whether a Bearer token was actually sent — it rides on the ApiError so a 401
 * handler can distinguish a token-present rejection from a request that went out with no token at all.
 * `renewalFailure` is the other half of that story: WHY the token was missing, captured by the caller at
 * the moment the getter returned null rather than looked up later, so a different renewal settling in
 * between cannot overwrite this request's verdict (see ApiError.renewalFailure).
 */
function toApiError(
  res: Response,
  body: unknown,
  authAttempted: boolean,
  renewalFailure: RenewalFailure | null,
): ApiError {
  const requestId = res.headers.get("X-Request-ID") ?? undefined;
  const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;

  const code =
    (typeof b.error === "string" && b.error) ||
    (typeof b.reason === "string" && b.reason) ||
    `http_${res.status}`;

  const message =
    (typeof b.message === "string" && b.message) ||
    (typeof b.detail === "string" && b.detail) ||
    (typeof body === "string" && body) ||
    res.statusText ||
    "Request failed";

  return {
    status: res.status,
    code,
    message,
    authAttempted,
    renewalFailure,
    ...(requestId !== undefined ? { requestId } : {}),
    ...(b.detail !== undefined && typeof b.detail !== "string" ? { detail: b.detail } : {}),
  };
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON request body (PATCH/POST); serialized + sets Content-Type. */
  body?: unknown;
  /** Skip the Authorization header (health endpoints). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };

  let authAttempted = false;
  let renewalFailure: RenewalFailure | null = null;
  if (!opts.anonymous) {
    const token = await tokenGetter();
    // Read the renewal verdict NOW, one line after the getter returned, and carry it on the error —
    // never look it up when the 401 comes back. It lives in a single global slot, and a renewal started
    // by another query milliseconds later is a separate auth0-spa-js call that can succeed and clear
    // that slot while this request is still in flight. Late-reading therefore returns null in exactly
    // the concurrent scenario the recovery exists for, and the user is ejected on a live session.
    renewalFailure = token ? null : readRenewalFailure();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      authAttempted = true;
    }
  }
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: opts.method ?? "GET",
      headers,
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (cause) {
    // Transport failure (offline, DNS, CORS) — status 0, never a silent undefined.
    throw new ApiRequestError({
      status: 0,
      code: "network_error",
      message: cause instanceof Error ? cause.message : "Network error",
    });
  }

  const body = await parseBody(res);
  if (!res.ok) throw new ApiRequestError(toApiError(res, body, authAttempted, renewalFailure));
  return body as T;
}

/**
 * Fetch a binary body (the invoice PDF stream) with the Bearer token. request<T>() JSON-parses, so the
 * PDF needs its own path: returns the Blob on 2xx, throws the normalized ApiError on non-2xx (so a 404
 * "PDF not available" or a 403 surfaces just like any other call).
 */
async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = await tokenGetter();
  const renewalFailure = token ? null : readRenewalFailure(); // captured at request time — see request()
  let authAttempted = false;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    authAttempted = true;
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "GET",
      headers,
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw new ApiRequestError({
      status: 0,
      code: "network_error",
      message: cause instanceof Error ? cause.message : "Network error",
    });
  }

  if (!res.ok)
    throw new ApiRequestError(toApiError(res, await parseBody(res), authAttempted, renewalFailure));
  return res.blob();
}

/** The BE's request-side ceiling — raised 500 → 5000 (21/7, parallel BE task, same branch). Always sent
 *  so the table's own result count reflects the TRUE total instead of a silently truncated page. */
export const POOL_USERS_REQUEST_LIMIT = 5000;

/** `accountId` (rev 6 R5/R6) is an EXACT filter (`account_id=`), combined with the free-text `q=`
 *  search rather than replacing it — see PoolUser's own banner in types.ts. `license` (rev 9, 21/7) is
 *  the raw wire value ("2"/"1"); omitted entirely means no filter (All) — never send "all" literally. */
function poolUsersQuery(q: string, accountId?: string, license?: "2" | "1"): string {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (accountId) params.set("account_id", accountId);
  if (license) params.set("license", license);
  params.set("limit", String(POOL_USERS_REQUEST_LIMIT));
  const s = params.toString();
  return s ? `?${s}` : "";
}

function poolAuditQuery(params: PoolAuditParams): string {
  const q = new URLSearchParams();
  if (params.email) q.set("email", params.email);
  if (params.actor) q.set("actor", params.actor);
  if (params.action) q.set("action", params.action);
  if (params.date_from) q.set("date_from", params.date_from);
  if (params.date_to) q.set("date_to", params.date_to);
  if (params.before_id) q.set("before_id", String(params.before_id));
  if (params.limit) q.set("limit", String(params.limit));
  const s = q.toString();
  return s ? `?${s}` : "";
}

function reportQuery(params: ReportParams): string {
  const q = new URLSearchParams();
  if (params.start) q.set("start", params.start);
  if (params.end) q.set("end", params.end);
  if (params.type) q.set("type", params.type);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Both params optional — an omitted one lets the server apply its own default (see
 *  PoolReportsDailyParams's own banner in types.ts). The Reports list screen always sends both. */
function poolReportsDailyQuery(params: PoolReportsDailyParams): string {
  const q = new URLSearchParams();
  if (params.start) q.set("start", params.start);
  if (params.end) q.set("end", params.end);
  const s = q.toString();
  return s ? `?${s}` : "";
}

function activityQuery(params: ActivityParams): string {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.kind) q.set("kind", params.kind);
  if (params.actor) q.set("actor", params.actor);
  if (params.date_from) q.set("date_from", params.date_from);
  if (params.date_to) q.set("date_to", params.date_to);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * Sync-now POSTs are special: the backend mirrors the outcome onto the HTTP status (200 ok/deduped,
 * 429 blocked_*, 502 error) but ALWAYS returns the structured envelope. We DON'T throw on 429/502 —
 * the caller must render the block + quota snapshot loudly. We only throw on a shape we don't
 * recognise (e.g. a 401/403 that has no envelope), so an auth failure still surfaces as an error.
 */
async function syncNow(path: string, signal?: AbortSignal): Promise<SyncNowEnvelope> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = await tokenGetter();
  const renewalFailure = token ? null : readRenewalFailure(); // captured at request time — see request()
  let authAttempted = false;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    authAttempted = true;
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers,
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw new ApiRequestError({
      status: 0,
      code: "network_error",
      message: cause instanceof Error ? cause.message : "Network error",
    });
  }

  const body = await parseBody(res);
  // Recognise a sync-now envelope by its `status` outcome field. The backend ALWAYS sends `quota`
  // alongside it (router.py:_envelope), but we deliberately don't REQUIRE it: a degraded path that
  // delivers a partial envelope (status + reason, quota truncated) must still render LOUDLY as that
  // outcome rather than collapsing to a thrown generic error that hides the reason. quota defaults to
  // null (envelopeToState + SyncNowMachine already treat it as "no snapshot to show"). A body with no
  // `status` (a proxy/auth 401/403/5xx with no envelope) still throws — there's no outcome to surface.
  if (body && typeof body === "object" && "status" in body) {
    const env = body as Partial<SyncNowEnvelope> & { status: SyncNowEnvelope["status"] };
    return { ...env, quota: env.quota ?? null } as SyncNowEnvelope;
  }
  // Not a sync-now envelope (auth/permission failure or unexpected) → throw normalized.
  throw new ApiRequestError(toApiError(res, body, authAttempted, renewalFailure));
}

/**
 * The roster sync ("Sync users now") POST is the same shape of special case as sync-now: the backend
 * mirrors the outcome onto the HTTP status (200 ok / 429 throttled / 409 already_running / 502
 * zoom_error|empty / 503 not_configured) but ALWAYS returns the structured envelope. We must NOT throw
 * on those, because each one is a DIFFERENT answer the operator has to read — "synced 199" and "the Zoom
 * call failed, the roster is untouched" are not the same event, and neither is a bare thrown Error.
 *
 * Recognition is by a STRING `status` field, not by res.ok: that is the whole point. A 401/403/proxy
 * page carries no envelope and still throws, so an auth failure never masquerades as a sync outcome.
 * A `status` string we don't recognise is NOT filtered out here — it is passed through and the panel
 * renders it as a failure (see RosterSyncResult.status).
 */
async function syncRoster(path: string): Promise<RosterSyncResult> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = await tokenGetter();
  const renewalFailure = token ? null : readRenewalFailure(); // captured at request time — see request()
  let authAttempted = false;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    authAttempted = true;
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { method: "POST", headers });
  } catch (cause) {
    throw new ApiRequestError({
      status: 0,
      code: "network_error",
      message: cause instanceof Error ? cause.message : "Network error",
    });
  }

  const body = await parseBody(res);
  if (
    body &&
    typeof body === "object" &&
    "status" in body &&
    typeof (body as { status: unknown }).status === "string"
  ) {
    return body as RosterSyncResult;
  }
  throw new ApiRequestError(toApiError(res, body, authAttempted, renewalFailure));
}

export const api = {
  me: (signal?: AbortSignal) => request<Me>("/api/v1/me", { ...(signal ? { signal } : {}) }),

  search: (q: string, signal?: AbortSignal) =>
    request<SearchResult>(`/api/v1/search?q=${encodeURIComponent(q)}`, {
      ...(signal ? { signal } : {}),
    }),

  customerOverview: (id: string, signal?: AbortSignal) =>
    request<CustomerOverview>(`/api/v1/customers/${encodeURIComponent(id)}`, {
      ...(signal ? { signal } : {}),
    }),

  customerSubscription: (id: string, signal?: AbortSignal) =>
    request<CustomerSubscription>(`/api/v1/customers/${encodeURIComponent(id)}/subscription`, {
      ...(signal ? { signal } : {}),
    }),

  customerUsage: (id: string, signal?: AbortSignal) =>
    request<CustomerUsage>(`/api/v1/customers/${encodeURIComponent(id)}/usage`, {
      ...(signal ? { signal } : {}),
    }),

  customerUsers: (id: string, signal?: AbortSignal) =>
    request<CustomerUsers>(`/api/v1/customers/${encodeURIComponent(id)}/users`, {
      ...(signal ? { signal } : {}),
    }),

  customerInvoices: (id: string, signal?: AbortSignal) =>
    request<CustomerInvoices>(`/api/v1/customers/${encodeURIComponent(id)}/invoices`, {
      ...(signal ? { signal } : {}),
    }),

  customerInvoiceDetail: (id: string, invoiceNumber: string, signal?: AbortSignal) =>
    request<InvoiceDetail>(
      `/api/v1/customers/${encodeURIComponent(id)}/invoices/${encodeURIComponent(invoiceNumber)}`,
      { ...(signal ? { signal } : {}) },
    ),

  /** The invoice PDF as a Blob (auth-bearing). The caller makes an object URL to view/download. */
  customerInvoicePdf: (id: string, invoiceNumber: string, signal?: AbortSignal) =>
    requestBlob(
      `/api/v1/customers/${encodeURIComponent(id)}/invoices/${encodeURIComponent(invoiceNumber)}/pdf`,
      signal,
    ),

  customerReports: (id: string, params: ReportParams = {}, signal?: AbortSignal) =>
    request<CustomerReport>(
      `/api/v1/customers/${encodeURIComponent(id)}/reports${reportQuery(params)}`,
      { ...(signal ? { signal } : {}) },
    ),

  /** The second Reports-tab report ("Insights") — same start/end/type contract as customerReports. */
  customerReportInsights: (id: string, params: ReportParams = {}, signal?: AbortSignal) =>
    request<CustomerReportInsights>(
      `/api/v1/customers/${encodeURIComponent(id)}/reports/insights${reportQuery(params)}`,
      { ...(signal ? { signal } : {}) },
    ),

  /** The activation badge (ADR ACTIVATION-BADGE). Served `Cache-Control: no-store`; polled by
   *  useCustomerActivation. A 404 (unknown account, or an API image without the route) is an ordinary
   *  ApiRequestError — the header degrades to "Activate: Unknown" rather than blanking. */
  customerActivation: (id: string, signal?: AbortSignal) =>
    request<CustomerActivation>(`/api/v1/customers/${encodeURIComponent(id)}/activation`, {
      ...(signal ? { signal } : {}),
    }),

  /** One round trip for every worklist count + the mv_account_360 refresh freshness (§5 of the worklist
   *  matview refactor). The server registers this route BEFORE `/worklists/{kind}` so "summary" is never
   *  captured as a kind. */
  worklistSummary: (signal?: AbortSignal) =>
    request<WorklistSummary>("/api/v1/worklists/summary", { ...(signal ? { signal } : {}) }),

  worklist: (kind: WorklistKind, signal?: AbortSignal) =>
    request<Worklist>(`/api/v1/worklists/${kind}`, { ...(signal ? { signal } : {}) }),

  /** The add-on seat-waste worklist — purpose-built typed rows (money columns stripped by role). */
  addonWasteWorklist: (signal?: AbortSignal) =>
    request<AddonWasteWorklist>("/api/v1/worklists/addon_waste", {
      ...(signal ? { signal } : {}),
    }),

  /** Lapsed trials — the PROSPECT list. Carries no money columns at all: nobody on it has ever paid. */
  lapsedTrialWorklist: (signal?: AbortSignal) =>
    request<LapsedTrialWorklist>("/api/v1/worklists/lapsed_trial", {
      ...(signal ? { signal } : {}),
    }),

  // --- admin: portal user management (Owner/Admin) ---
  adminUsers: (signal?: AbortSignal) =>
    request<AdminUsers>("/api/v1/admin/users", { ...(signal ? { signal } : {}) }),

  setUserRole: (userId: string, body: RoleUpdateBody) =>
    request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(userId)}/role`, {
      method: "PATCH",
      body,
    }),

  setUserActive: (userId: string, body: ActiveUpdateBody) =>
    request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(userId)}/active`, {
      method: "PATCH",
      body,
    }),

  /** Invite (or RE-invite) a roster member. This is the ONE call that clears a revocation tombstone —
   *  `revoked_cleared: true` means the operator just let a previously-DELETED person back in. */
  inviteUser: (email: string) =>
    request<InviteResult>("/api/v1/admin/users/invite", { method: "POST", body: { email } }),

  /** Withdraw a pending invite (clears invited_at). Does not block sign-in — the roster is the gate. */
  cancelInvite: (email: string) =>
    request<{ email: string; invited_at: string | null }>("/api/v1/admin/users/invite/cancel", {
      method: "POST",
      body: { email },
    }),

  /**
   * Remove a DISABLED user from the Portal user list: the app_user row is hard-deleted and their email
   * is tombstoned, so they fall back to being a plain Zoom-roster entry and only an explicit Invite can
   * bring them back. Every guardrail is server-side and lands as a 4xx that THROWS here, message intact:
   *   404 no such user · 409 "disable the user first…" / "you cannot delete your own account" /
   *   "cannot remove the last active Owner" · 403 "only an Owner can delete an Owner".
   * The UI pre-flights the same rules to explain the refusal BEFORE the click — but this is the gate.
   */
  deleteUser: (userId: string) =>
    request<DeleteUserResult>(`/api/v1/admin/users/${encodeURIComponent(userId)}`, {
      method: "DELETE",
    }),

  /** "Sync users now" — pull the staff Zoom roster on demand. Structured outcome, never throws on a
   *  refusal/failure; see syncRoster(). The roster ALSO syncs daily at 02:30 BKK and on worker boot. */
  syncRoster: () => syncRoster("/api/v1/admin/users/sync-roster"),

  // --- activity feed (Owner/Admin) — keyset-paged ---
  activity: (params: ActivityParams = {}, signal?: AbortSignal) =>
    request<ActivityFeed>(`/api/v1/activity${activityQuery(params)}`, {
      ...(signal ? { signal } : {}),
    }),

  /** Mirror-health (poller-current) — separate from data freshness. No HTTP route yet; mock-served. */
  mirrorHealth: (signal?: AbortSignal) =>
    request<MirrorHealth>("/api/v1/mirror-health", { ...(signal ? { signal } : {}) }),

  health: (signal?: AbortSignal) =>
    request<HealthReady>("/health/ready", { anonymous: true, ...(signal ? { signal } : {}) }),

  /** Validate a portal invite token (anonymous — the accept-invite public screen runs pre-auth).
   *  Resolves the invitee's email on a valid token; a non-2xx (expired/unknown token) throws. */
  validateInvite: (token: string, signal?: AbortSignal) =>
    request<{ email: string }>(`/api/v1/invite/validate?token=${encodeURIComponent(token)}`, {
      anonymous: true,
      ...(signal ? { signal } : {}),
    }),

  // --- sync-now (live Zoom) — returns the structured envelope, never throws on 429/502 ---
  syncPlansUsage: (id: string, signal?: AbortSignal) =>
    syncNow(`/api/v1/sync-now/customers/${encodeURIComponent(id)}/plans-usage`, signal),

  syncUsers: (id: string, signal?: AbortSignal) =>
    syncNow(`/api/v1/sync-now/customers/${encodeURIComponent(id)}/users`, signal),

  syncAccounts: (signal?: AbortSignal) => syncNow("/api/v1/sync-now/accounts", signal),

  // --- Pool module (Zoom license/period time-sharing, Phase 1) — allow-list gated; see types.ts ---

  /** Accounts eligible to become a pool (search by name/number). */
  poolCandidates: (q: string, signal?: AbortSignal) =>
    request<PoolAccountCandidates>(`/api/v1/pool/accounts/candidates?q=${encodeURIComponent(q)}`, {
      ...(signal ? { signal } : {}),
    }),

  poolAccounts: (signal?: AbortSignal) =>
    request<PoolAccounts>("/api/v1/pool/accounts", { ...(signal ? { signal } : {}) }),

  /** 201 on success; 409 already a pool; 404 not in the ZPCD mirror — all throw ApiRequestError. */
  poolAddAccount: (accountId: string) =>
    request<PoolAddAccountResult>("/api/v1/pool/accounts", {
      method: "POST",
      body: { account_id: accountId },
    }),

  /** Toggle a pool's enabled flag. Disabling FREEZES the pool (sweep stops touching its membership and
   *  periods) — it is not a delete, and the confirm dialog on the Settings screen says so. */
  poolSetAccountEnabled: (accountId: string, enabled: boolean) =>
    request<{ account_id: string; enabled: boolean }>(
      `/api/v1/pool/accounts/${encodeURIComponent(accountId)}/enabled`,
      { method: "PATCH", body: { enabled } },
    ),

  /** rev 15 — re-verify a STANDALONE account's stored Zoom credential live (token + one bare read).
   *  Success bumps last_verified_at; 409 not standalone; 422 the credential failed the live check
   *  (cause-classified, rendered verbatim); 503 no POOL_CRED_KEY. */
  poolCredentialTest: (accountId: string) =>
    request<{ ok: boolean; verified_at: string }>(
      `/api/v1/pool/accounts/${encodeURIComponent(accountId)}/credential-test`,
      { method: "POST" },
    ),

  /** rev 15.2 — operator-entered Zoom subscription PERIOD (start+end) for a STANDALONE account
   *  (both null clears it). 409 on a master/mirror account; 422 on a half-filled range or
   *  end<start (validated server-side). */
  poolSetSubscriptionPeriod: (
    accountId: string,
    subscriptionStart: string | null,
    subscriptionEnd: string | null,
  ) =>
    request<{
      account_id: string;
      zoom_subscription_start: string | null;
      zoom_subscription_end: string | null;
    }>(`/api/v1/pool/accounts/${encodeURIComponent(accountId)}/subscription-period`, {
      method: "PATCH",
      body: { subscription_start: subscriptionStart, subscription_end: subscriptionEnd },
    }),

  /** rev 16 — operator-entered Account Type + Seats for a STANDALONE account (each field
   *  independently settable/clearable — null clears just that one). 409 on a master/mirror
   *  account; 422 on an account_type outside the four tierOf() tiers or non-positive seats. */
  poolSetManualAccountInfo: (accountId: string, accountType: string | null, seats: number | null) =>
    request<{
      account_id: string;
      zoom_account_type: string | null;
      zoom_seats: number | null;
    }>(`/api/v1/pool/accounts/${encodeURIComponent(accountId)}/manual-info`, {
      method: "PATCH",
      body: { account_type: accountType, seats },
    }),

  /** Removes the account from the pool (rev 7) — sweep stops watching it and its observed membership
   *  rows are dropped; service periods and audit history are kept. 404 = no such pool account. */
  poolRemoveAccount: (accountId: string) =>
    request<PoolRemoveAccountResult>(`/api/v1/pool/accounts/${encodeURIComponent(accountId)}`, {
      method: "DELETE",
    }),

  poolUsers: (q: string, accountId?: string, license?: "2" | "1", signal?: AbortSignal) =>
    request<PoolUsers>(`/api/v1/pool/users${poolUsersQuery(q, accountId, license)}`, {
      ...(signal ? { signal } : {}),
    }),

  poolCustomerHistory: (email: string, signal?: AbortSignal) =>
    request<PoolCustomerHistory>(`/api/v1/pool/customers/${encodeURIComponent(email)}/history`, {
      ...(signal ? { signal } : {}),
    }),

  /** 201; 409 = an open period already exists for this email (edit the existing one instead). */
  poolCreatePeriod: (body: PoolCreatePeriodBody) =>
    request<PoolCreatePeriodResult>("/api/v1/pool/periods", { method: "POST", body }),

  // Mutation response shapes below mirror the REAL FastAPI bodies (verified against the backend
  // services) — they are thin ack objects, NOT the full row; every consumer refetches via
  // invalidateQueries instead of trusting a body echo.
  poolUpdatePeriod: (periodId: number, body: PoolUpdatePeriodBody) =>
    request<{ period_id: number; changed: boolean }>(`/api/v1/pool/periods/${periodId}`, {
      method: "PATCH",
      body,
    }),

  /** Sets what happens AT expiry. 422 on a bad combination (e.g. grace_2stage with no grace_days) —
   *  the caller renders the server message verbatim, same as every other refusal in this module.
   *
   *  30/7: a renew=true body carries `next_period_end` and normally comes back with the PLANNED round
   *  (`rolled_forward: false`) for the cron to promote on the day. `rolled_forward: true` means the
   *  current round had already expired so the server opened the new one straight away — the caller has
   *  to tell those apart, or it reports "saved the plan" about a period that no longer exists. */
  poolRenewDecision: (periodId: number, body: PoolRenewDecisionBody) =>
    request<PoolRenewDecisionResult>(`/api/v1/pool/periods/${periodId}/renew-decision`, {
      method: "POST",
      body,
    }),

  /** Renews NOW: closes the current period and opens a new one with the given dates. */
  poolRenew: (periodId: number, body: PoolRenewBody) =>
    request<PoolRenewResult>(`/api/v1/pool/periods/${periodId}/renew`, { method: "POST", body }),

  /** Closes a period. A reason is required — this is the business record of why it ended. */
  poolClosePeriod: (periodId: number, reason: string) =>
    request<{ period_id: number; closed: boolean }>(`/api/v1/pool/periods/${periodId}/close`, {
      method: "POST",
      body: { reason } satisfies PoolCloseBody,
    }),

  /** Add a pool account STANDALONE, off a live-verified Zoom S2S credential (rev 6 R4) — 503 no
   *  `POOL_CRED_KEY`, 422 the live verify failed, 409 already a pool — all rendered verbatim. */
  poolAddAccountFromCredential: (body: PoolAddCredentialBody) =>
    request<PoolAddAccountResult>("/api/v1/pool/accounts/credential", { method: "POST", body }),

  /** `_manage` only (Owner or pool-Admin — see types.ts). `owners` is always pinned above `operators`. */
  poolOperators: (signal?: AbortSignal) =>
    request<PoolOperators>("/api/v1/pool/operators", { ...(signal ? { signal } : {}) }),

  /** 422 when `email` already belongs to a portal Owner — rendered verbatim (rev 6 R1). */
  poolAddOperator: (body: PoolAddOperatorBody) =>
    request<{ email: string; role: PoolOperatorRole }>("/api/v1/pool/operators", {
      method: "POST",
      body,
    }),

  poolRemoveOperator: (email: string) =>
    request<{ email: string; revoked: boolean }>(
      `/api/v1/pool/operators/${encodeURIComponent(email)}`,
      { method: "DELETE" },
    ),

  /** Search `public.app_user` by email to back the Settings "grant access" picker (rev 6 R3). Below
   *  MIN_CANDIDATE_CHARS the caller disables the query rather than fetching a near-empty term. */
  poolUserCandidates: (q: string, signal?: AbortSignal) =>
    request<PoolUserCandidates>(`/api/v1/pool/user-candidates?q=${encodeURIComponent(q)}`, {
      ...(signal ? { signal } : {}),
    }),

  /** The two mismatch cards — the module's headline value (design §10). */
  poolDashboard: (signal?: AbortSignal) =>
    request<PoolDashboard>("/api/v1/pool/dashboard", { ...(signal ? { signal } : {}) }),

  /** Feeds FreshnessBadge on the Accounts + Dashboard screens (last_complete.finished_at). */
  poolStatus: (signal?: AbortSignal) =>
    request<PoolStatus>("/api/v1/pool/status", { ...(signal ? { signal } : {}) }),

  /** "Sync now" (rev 8, Pool Accounts header) — enqueues an out-of-cadence sweep run. 409 (a sweep is
   *  already running) throws like any other refusal; the caller renders it as an informational toast,
   *  not an error (see useRunPoolSweep). */
  poolSweepRun: () => request<PoolSweepEnqueueResult>("/api/v1/pool/sweep/run", { method: "POST" }),

  poolAudit: (params: PoolAuditParams = {}, signal?: AbortSignal) =>
    request<PoolAudit>(`/api/v1/pool/audit${poolAuditQuery(params)}`, {
      ...(signal ? { signal } : {}),
    }),

  // --- Pool module Reports tab ("Online account history", 30/7 — see types.ts's own banner) ---------

  poolReportsDaily: (params: PoolReportsDailyParams = {}, signal?: AbortSignal) =>
    request<PoolReportsDaily>(`/api/v1/pool/reports/daily${poolReportsDailyQuery(params)}`, {
      ...(signal ? { signal } : {}),
    }),

  /** 404 = no snapshot for that date (thrown as ApiRequestError, status 404) — the day-view renders a
   *  dedicated "no snapshot" state for it, distinct from a generic error. */
  poolReportsDay: (date: string, signal?: AbortSignal) =>
    request<PoolReportDayDetail>(`/api/v1/pool/reports/daily/${encodeURIComponent(date)}`, {
      ...(signal ? { signal } : {}),
    }),

  // --- Pool module Phase 2: intents queue + force actions (see types.ts's Phase 2 banner) -----------

  /** Every OPEN (unresolved) two-phase intent — a crashed worker leaves one of these behind. */
  poolIntents: (signal?: AbortSignal) =>
    request<PoolIntents>("/api/v1/pool/intents", { ...(signal ? { signal } : {}) }),

  /** A human declares what Zoom actually did. 404 already resolved; 409 missing period_id; 422 blank
   *  note — all thrown as ApiRequestError and rendered verbatim by the caller. */
  poolResolveIntent: (auditId: number, body: PoolResolveIntentBody) =>
    request<PoolResolveIntentResult>(`/api/v1/pool/intents/${auditId}/resolve`, {
      method: "POST",
      body,
    }),

  /** Forces an immediate license removal, skipping the sweep's cadence. 404 no open period; 409 an open
   *  intent already exists for this period; 503 the Zoom app has no S2S credentials configured. */
  poolForceLicenseRemove: (periodId: number) =>
    request<PoolForceActionResult>(`/api/v1/pool/periods/${periodId}/force-license-remove`, {
      method: "POST",
    }),

  /** Forces an immediate unlink. Same 404/409/503 as above, PLUS a 409 when the pool has (or the
   *  managed-domain check is unknown/stale) managed domains — rendered verbatim, never swallowed. */
  poolForceUnlink: (periodId: number) =>
    request<PoolForceActionResult>(`/api/v1/pool/periods/${periodId}/force-unlink`, {
      method: "POST",
    }),

  /** rev 17b — the membership-keyed (period-less) bulk/single-unlink primitive; PINNED CONTRACT, see
   *  types.ts's PoolBulkForceResult banner. `action` uses the server's underscore enum
   *  ("license_remove", not "license-remove") — never re-hyphenate it at a call site. Throws on every
   *  request-level refusal (503 kill-switch off, 409 stale sweep, 422 size/empty/unknown action); a
   *  200 body still carries per-email skips/failures in `results`, never thrown. */
  poolBulkForce: (action: PoolBulkForceAction, emails: string[]) =>
    request<PoolBulkForceResult>("/api/v1/pool/users/bulk-force", {
      method: "POST",
      body: { action, emails },
    }),

  // --- Pool module: self-service access check + system control (rev 6) ------------------------------

  /** Never 403s — answers `role: null` for a non-operator. Drives nav visibility + every tab/section
   *  gate off `role`/`open_intents` (see types.ts's PoolAccess banner). */
  poolAccess: (signal?: AbortSignal) =>
    request<PoolAccess>("/api/v1/pool/access", { ...(signal ? { signal } : {}) }),

  /** `_manage` only (403 for a pool-Viewer or a non-operator — see types.ts's PoolAccess banner). */
  poolSystemState: (signal?: AbortSignal) =>
    request<PoolSystemState>("/api/v1/pool/system-state", { ...(signal ? { signal } : {}) }),

  /** 422 when arming LIVE without confirm:"LIVE"; 409 while hard_disabled — both rendered verbatim. */
  poolSetSystemState: (body: PoolSetSystemStateBody) =>
    request<PoolSetSystemStateResult>("/api/v1/pool/system-state", { method: "PUT", body }),
};

export type Api = typeof api;
